// Central data client.
//
// Production playback is now 100% static: the frontend fetches
// precomputed JSON files under /data/ that ship with the Vite build.
// No Cloudflare Worker or D1 database is consulted at runtime. This
// replaces the old `/api/day/:date` dynamic computation that was
// overwhelming Worker CPU/memory limits during playback.
//
// File layout under `/data/`:
//   /data/manifest.json           { dates, state_colors, phone_shard_count, ... }
//   /data/days/YYYY-MM-DD.json    DayDot[] for that playable day
//   /data/phones/shard-NNN.json   { [phone_id]: PhoneHistoryEntry[] } where
//                                 NNN = phone_id % phone_shard_count (zero-padded)
//
// See `scripts/build-static-data.mjs` for the generator and AGENT.md for
// the contract.
//
// Demo mode: when Vite is built or run with VITE_REDDOTMAP_DEMO=1 we
// resolve from local fixtures (`demoFixtures.ts`) instead of hitting the
// static files. Production `npm run build` leaves this flag unset and
// all data loads are static fetches relative to the site root.

import type {
  DayDot,
  ISODate,
  PhoneHistoryEntry,
} from './types';

/**
 * Deprecated. The hosted Worker origin used in the previous architecture.
 * Kept only as a documentation breadcrumb \u2014 nothing in this module reads
 * from it anymore. Remove once external tooling stops referencing it.
 */
export const API_BASE = 'https://nationalmap.deliverfund.ai';

/** True when the sandbox demo mode is active. */
export const DEMO_MODE: boolean =
  import.meta.env.VITE_REDDOTMAP_DEMO === '1' ||
  import.meta.env.VITE_REDDOTMAP_DEMO === 'true';

// Dynamic-import singleton for the demo fixtures. We only load the fixture
// module when DEMO_MODE is truthy at build time, so the production bundle
// does not ship the fixture data. Vite splits this into a chunk that is
// never fetched by the production path.
let demoMod: Promise<typeof import('./demoFixtures')> | null = null;
function loadDemo(): Promise<typeof import('./demoFixtures')> {
  if (!demoMod) demoMod = import('./demoFixtures');
  return demoMod;
}

// Small artificial latency so loading states are observable in demo
// preview. Low enough that interactions still feel instant.
const DEMO_LATENCY_MS = 80;

function demoResolve<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), DEMO_LATENCY_MS));
}

/**
 * Data is shipped alongside the Vite build, so all paths are resolved
 * relative to the deployed document URL. `import.meta.env.BASE_URL` is
 * "./" in this project (see `vite.config.ts`), which is exactly what we
 * want for Cloudflare Pages (served at the site root) and for nested
 * preview proxy URLs.
 */
function dataUrl(rel: string): string {
  const base = import.meta.env.BASE_URL || './';
  // Trim any leading slash on `rel` so the concat is predictable.
  const cleaned = rel.replace(/^\/+/, '');
  return `${base}data/${cleaned}`;
}

async function getJSON<T>(url: string): Promise<T> {
  let res: Response;
  try {
    // Static files are content-addressed by date; cache defaults are fine.
    res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
  } catch (err) {
    console.error(`[data] network error for ${url}:`, err);
    throw new Error(`Network error fetching ${url}.`);
  }
  if (!res.ok) {
    const msg = `HTTP ${res.status} ${res.statusText} from ${url}`;
    console.error(`[data] ${msg}`);
    throw new Error(msg);
  }
  try {
    return (await res.json()) as T;
  } catch (err) {
    console.error(`[data] invalid JSON from ${url}:`, err);
    throw new Error(`Invalid JSON from ${url}`);
  }
}

/**
 * Manifest shape (subset we care about at runtime). The generator emits
 * extra QA fields; they are ignored here.
 */
export interface Manifest {
  dates: ISODate[];
  first_date: ISODate;
  last_date: ISODate;
  total_days: number;
  phone_shard_count: number;
  state_colors?: Record<string, string>;
  /** Lower-48 state names (alphabetical). Emitted by build-static-data.mjs. */
  lower_48_states?: string[];
  /** Subset of lower_48_states that at least one phone is assigned to as origin. */
  origin_states_present?: string[];
}

/** Public manifest fetcher. Used by the UI to populate the state filter. */
export async function fetchManifest(): Promise<Manifest> {
  if (DEMO_MODE) {
    const m = await loadDemo();
    return demoResolve(m.demoManifest());
  }
  return getManifest();
}

// Manifest cache (one-shot; the underlying file is immutable for a given
// deploy so the browser's HTTP cache handles revalidation fine).
let manifestPromise: Promise<Manifest> | null = null;
function getManifest(): Promise<Manifest> {
  if (!manifestPromise) {
    manifestPromise = getJSON<Manifest>(dataUrl('manifest.json'));
  }
  return manifestPromise;
}

export async function fetchDays(): Promise<ISODate[]> {
  if (DEMO_MODE) {
    const m = await loadDemo();
    return demoResolve(m.demoDays());
  }
  const manifest = await getManifest();
  return manifest.dates;
}

export async function fetchDay(date: ISODate): Promise<DayDot[]> {
  if (DEMO_MODE) {
    const m = await loadDemo();
    return demoResolve(m.demoDay(date));
  }
  return getJSON<DayDot[]>(dataUrl(`days/${encodeURIComponent(date)}.json`));
}

// Phone-history fetches hit a shard file keyed by phone_id. Each shard is
// a JSON object of the form { [phone_id]: PhoneHistoryEntry[] }, so one
// shard covers ~420 phones on average. We cache already-fetched shards
// so subsequent dot clicks into the same shard are instant.
const shardCache = new Map<number, Promise<Record<string, PhoneHistoryEntry[]>>>();
function getShard(shardIdx: number, padded: string): Promise<Record<string, PhoneHistoryEntry[]>> {
  const cached = shardCache.get(shardIdx);
  if (cached) return cached;
  const p = getJSON<Record<string, PhoneHistoryEntry[]>>(dataUrl(`phones/shard-${padded}.json`));
  shardCache.set(shardIdx, p);
  return p;
}

export async function fetchPhone(id: number): Promise<PhoneHistoryEntry[]> {
  if (!Number.isFinite(id) || id < 0) {
    return Promise.reject(new Error(`Invalid phone id: ${id}`));
  }
  if (DEMO_MODE) {
    const m = await loadDemo();
    return demoResolve(m.demoPhoneHistory(id));
  }
  const manifest = await getManifest();
  const count = manifest.phone_shard_count || 256;
  const shardIdx = ((id % count) + count) % count;
  const padded = String(shardIdx).padStart(3, '0');
  const shard = await getShard(shardIdx, padded);
  const entries = shard[String(id)];
  // Absent phone \u2192 empty history (UI handles the empty-state).
  return entries ?? [];
}
